import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { compact, USD } from '../../lib/format';
import { C, ink } from '../../lib/theme';
import type { RecoveredWeekRow } from '../../lib/types';
import { ChartEmpty, ChartTooltip, Legend, axisStyle, gridStroke } from './shared';

const LABELS: Record<'vencido' | 'falha' | 'contraction', string> = {
  vencido: 'Vencido (churn)',
  falha: 'Falha de pagamento',
  contraction: 'Contraction',
};
const COLORS = { vencido: C.churn, falha: C.blueSoft, contraction: C.amber } as const;

function weekLabel(weekStart: string): string {
  const d = new Date(weekStart);
  if (Number.isNaN(d.getTime())) return weekStart;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
}

/**
 * Comparativo semanal por colunas (pedido explícito, 2026-09-14) — uma
 * semana = um grupo de 3 barras (Vencido/Falha/Contraction), pra ver em
 * qual categoria a recuperação está concentrada semana a semana, não só o
 * total do período inteiro. `active` filtra quais categorias aparecem,
 * mesmo Set<RecoveredCategory> que os tiles de filtro do painel usam —
 * uma barra "desligada" simplesmente não é passada pro `<Bar>` correspondente
 * em vez de ficar cinza (grupo de barra fica mais estreito, não empilhado).
 */
export function RecoveredWeeklyChart({ rows, active }: {
  rows: RecoveredWeekRow[];
  active: Set<'vencido' | 'falha' | 'contraction'>;
}) {
  const data = useMemo(
    () => rows.map((r) => ({
      week: r.week_start,
      label: weekLabel(r.week_start),
      vencido: Number(r.vencido_valor ?? 0),
      falha: Number(r.falha_valor ?? 0),
      contraction: Number(r.contraction_valor ?? 0),
    })),
    [rows],
  );

  if (data.length === 0) return <ChartEmpty text="Sem semana com recuperação no período filtrado." />;

  const categories = (['vencido', 'falha', 'contraction'] as const).filter((c) => active.has(c));

  return (
    <>
      <ResponsiveContainer width="100%" height="88%">
        <BarChart data={data} margin={{ top: 6, right: 4, left: -14, bottom: 0 }}>
          <CartesianGrid stroke={gridStroke} vertical={false} />
          <XAxis dataKey="label" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => compact(v)} width={52} />
          <Tooltip
            cursor={{ fill: ink(0.05) }}
            content={({ active: hovering, payload, label }) =>
              hovering && payload?.length ? (
                <ChartTooltip
                  label={`Semana de ${String(label)}`}
                  rows={payload.map((p) => ({ name: String(p.name), value: USD(p.value as number), color: String(p.color) }))}
                />
              ) : null
            }
          />
          {categories.map((c) => (
            <Bar key={c} dataKey={c} name={LABELS[c]} fill={COLORS[c]} radius={[4, 4, 0, 0]} maxBarSize={28} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <Legend items={categories.map((c) => ({ label: LABELS[c], color: COLORS[c] }))} />
    </>
  );
}
